/**
 * time.js — Europe/Berlin display formatters + the repo's central timestamp parser.
 *
 * All timestamps in admin / operator-facing UI go through these helpers so
 * Samuel and Nils see a consistent timezone regardless of where they're
 * browsing from. Backend stores UTC (correct); browser-local timezones aren't
 * reliable (we may be on the road). Single source of truth: Europe/Berlin.
 *
 * Uses `Intl.DateTimeFormat` with `timeZone: 'Europe/Berlin'` — DST shifts
 * (CEST/CET) are handled automatically by the browser.
 *
 * Backend logs continue to print UTC (ops standard). Frontend everywhere a
 * human reads a timestamp goes through here.
 *
 * Usage:
 *   import { formatBerlinDateTime, formatBerlinDate, formatBerlinTime, formatBerlinRelative } from '~/utils/time';
 *   formatBerlinDateTime('2026-05-11T19:37:55Z')  → '11.05.2026, 21:37'
 *   formatBerlinDate('2026-05-11T19:37:55Z')      → '11.05.2026'
 *   formatBerlinTime('2026-05-11T19:37:55Z')      → '21:37'
 *   formatBerlinRelative(dateBefore)              → 'vor 3 Minuten' / 'Gestern' / '11.05.2026, 21:37'
 *
 * ── Track A5-4a (2026-08-03): shape-aware parsing ────────────────────────────
 * `parseTimestamp()` replaced a blank `new Date(input)`. That blank call fixed
 * the RENDERING but not the PARSING: an offsetless string ("2026-07-15T22:30:00",
 * which is what a naive-UTC column serialises to) was read as BROWSER-LOCAL and
 * landed the browser's offset away — 2 h in a Berlin summer — before it was ever
 * formatted. The parser now branches on the SHAPE of the value:
 *
 *   1. offsetless full timestamp  → read as UTC (append `Z`)
 *   2. timestamp WITH an offset   → parsed unchanged (native `Date` is correct)
 *   3. bare `YYYY-MM-DD`          → a CALENDAR DATE, anchored at UTC midnight
 *   4. RFC 822 / HTTP-date        → parsed unchanged (its `GMT`/`UTC` IS an offset)
 *
 * Branch 4 was added on 2026-10-30. Flask's default JSON provider serialises a
 * `datetime` with `http_date()` — `"Sat, 01 Aug 2026 15:01:03 GMT"` — and the
 * raw-SQL endpoints (`market_analytics_api.py` `/signals`) hand the psycopg2
 * datetime straight to `jsonify`. Before this branch, `HAS_OFFSET_RE` did not
 * recognise the trailing `GMT`, the parser appended `Z` to it
 * (`"… GMTZ"`), `new Date()` returned Invalid, and every `detected_at` on
 * Market Lab rendered as the placeholder `—` although the column held a value
 * (measured: 61 of 61 rows non-null). It is a WIDENING only: a value that
 * parsed before parses to the same instant.
 *
 * Branch 3 is the one the naive "just append `Z` everywhere" fix gets wrong. A
 * date column (`sla_due_date`, `order_date`, `reorder_date`, `expected_arrival`,
 * `eta`, `deadline_date`) names a day, not an instant; it has no time of day to
 * shift. The anchor is UTC midnight rather than LOCAL midnight because every
 * formatter here renders in Europe/Berlin, which is always AHEAD of UTC — so a
 * UTC-midnight anchor names the same calendar day for every viewer, while a
 * local-midnight anchor slips a day for anyone east of UTC+2.
 *
 * The offset-detection regex is deliberately the SAME spelling as
 * `frontend/src/rma/lib/slaSummary.js` `parseDueAt` (R15-B1) — that file's
 * `parseDueAt` now delegates here, so the repo has ONE rule, not two dialects.
 */

const BERLIN_TZ = 'Europe/Berlin';
const LOCALE = 'de-DE';

/**
 * A trailing UTC designator or numeric offset: `Z`, `+02:00`, `-0500`, and the
 * named forms `GMT` / `UTC`.
 *
 * 🔴 `GMT`/`UTC` are not decoration (R15-W3, 2026-10-30): Flask's `jsonify`
 * serialises a raw `datetime` as an HTTP date (`"Sat, 01 Aug 2026 15:01:03 GMT"`,
 * `docs/knowledge/operations/data-quirks.md`), and without them the parser
 * appended a `Z` (`"… GMTZ"`), `new Date` returned Invalid, and every such
 * value rendered as the `—` placeholder.
 *
 * The rule lives here and ONLY here: `slaSummary.js` `parseDueAt` no longer has
 * a spelling of its own — since Track A5-4a it delegates to `parseTimestamp`
 * (`frontend/src/rma/lib/slaSummary.js:152-155`), so widening this regex widens
 * both callers at once.
 */
const HAS_OFFSET_RE = /(Z|[+-]\d{2}:?\d{2}|GMT|UTC)$/i;

/** A bare calendar date with NO time component at all. */
const BARE_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Leading Y-M-D of any ISO-ish value (prefix match, like `localDateOnly`). */
const YMD_PREFIX_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * The raw-SQL / psycopg spelling uses a SPACE between date and time
 * ("2026-07-15 22:30:00") instead of the ISO `T`. That form is NOT an
 * ECMAScript Date Time String, so `new Date()` handling of it — and of
 * anything appended to it — is implementation-defined: V8 accepts it, other
 * engines are free not to. Normalising to `T` first means the value we hand
 * to `new Date()` is always spec-shaped, in every browser.
 */
const SQL_SPACE_SEPARATOR_RE = /^(\d{4}-\d{2}-\d{2}) (?=\d{2}:)/;

/**
 * The fractional-seconds part of a time, if any. ECMA-262's Date Time String
 * Format allows EXACTLY three fraction digits. Python's default
 * `datetime.isoformat()` emits SIX (microseconds) — and that is what most of
 * this app's naive timestamps actually look like, e.g. the bare `.isoformat()`
 * calls on `created_at` / `updated_at` in `backend/rma_routes.py`. Appending
 * `Z` to `…01.998326` yields an out-of-spec string whose parse is
 * implementation-defined: V8 truncates, other engines need not.
 *
 * So the fraction is normalised to exactly three digits before it ever reaches
 * `new Date()`. TRUNCATED, not rounded: rounding `…59.999999` would carry into
 * the next second, and no display in this app is worth moving an instant for.
 */
const FRACTION_RE = /\.(\d+)/;

/** Below this many seconds of distance, "Gerade eben" beats a second count. */
const JUST_NOW_SEC = 10;

/** Beyond this many calendar days in either direction, render an absolute date. */
const DAY_WINDOW = 7;

/**
 * The two relative word sets (G12-V). `'de'` is the default and reproduces the
 * pre-G12 output BYTE FOR BYTE — the German strings below are the ones the
 * rungs returned inline before this object existed. `'en'` is the opt-in for
 * the first English surface (C4 `/rma/faelligkeit`).
 *
 * 🔴 **Only the RELATIVE readings switch.** The absolute fallbacks
 * (`formatBerlinDate` / `formatBerlinDateTime`) stay `de-DE` — which is why
 * `CaseTime`'s prop is called `relativeLocale` and not `locale` (Grill W5).
 *
 * 🔴 **`JUST_NOW_SEC` has its own word in each set** (Grill W-R2-14): without
 * it the English branch would fall through to `Intl.RelativeTimeFormat` and
 * read "9 seconds ago" — a behaviour change, not a language one.
 *
 * 🔴 **Capitalisation is a standalone-column choice, not a sentence-start one.**
 * `Today` / `Yesterday` / `Tomorrow` are capitalised because they stand ALONE in
 * a table cell (matching `conversationView.js`'s day labels); `just now` is
 * lowercase because the existing English source for the same reading
 * (`rma/utils/relativeTime.js`) writes it lowercase, and the sub-day readings
 * follow `Intl`'s lowercase habit. **Do not put these words inline behind a
 * label** ("Since Yesterday"): that is a typographic error in English. A caller
 * that needs them inline must split label and value into separate elements
 * (W2, Regel-16 Runde 1; G12b must not render "Since Yesterday").
 */
const RELATIV_WORTE = {
  de: {
    rtf: LOCALE,
    justNow: 'Gerade eben',
    today: 'Heute',
    yesterday: 'Gestern',
    tomorrow: 'Morgen',
    pastDays: (n) => `vor ${n} Tagen`,
    futureDays: (n) => `in ${n} Tagen`,
  },
  en: {
    rtf: 'en',
    justNow: 'just now',
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',
    pastDays: (n) => `${n} days ago`,
    futureDays: (n) => `in ${n} days`,
  },
};

/**
 * Coerce a calendar-date-ish value to a Date anchored at UTC midnight of the
 * day it names. Returns null when no `YYYY-MM-DD` prefix is present.
 *
 * Deliberately NOT `new Date(y, m, d)` (browser-local midnight, the
 * `slaSummary.js localDateOnly` spelling): that anchor is correct for
 * local-day ARITHMETIC but slips a calendar day when rendered in a FIXED
 * timezone from a browser east of it. Everything in this file renders in
 * Europe/Berlin, so the anchor has to be timezone-free.
 */
function _dateOnlyToDate(raw) {
  const m = YMD_PREFIX_RE.exec(raw);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Shape-aware parse of a timestamp-ish value → Date, or null when
 * missing/unparseable. The central parser for the whole frontend.
 *
 * @param {Date|string|number|null|undefined} input
 * @param {{dateOnly?: boolean}} [opts]
 *   `dateOnly: true` forces branch 3 even when the value carries a time of day
 *   (a date column that was serialised as a timestamp). Use it for date
 *   columns; without it a `…T23:30:00` value is a real instant and legitimately
 *   renders as the NEXT Berlin day.
 * @returns {Date|null}
 */
export function parseTimestamp(input, { dateOnly = false } = {}) {
  if (input == null || input === '') return null;
  if (input instanceof Date) return Number.isFinite(input.getTime()) ? input : null;
  if (typeof input === 'number') {
    const d = new Date(input);
    return Number.isFinite(d.getTime()) ? d : null;
  }
  const raw = String(input).trim();
  if (!raw) return null;

  // Branch 3 — a calendar date, not an instant.
  if (dateOnly || BARE_DATE_RE.test(raw)) return _dateOnlyToDate(raw);

  // Branches 1 + 2 — an instant. Bring the value into the shape ECMA-262
  // actually specifies BEFORE handing it to `new Date()`: raw-SQL space
  // separator -> `T`, and any fractional seconds -> exactly three digits.
  // Both normalisations run regardless of whether an offset is present; an
  // out-of-spec string is out of spec either way.
  const iso = raw
    .replace(SQL_SPACE_SEPARATOR_RE, '$1T')
    .replace(FRACTION_RE, (_, digits) => `.${`${digits}000`.slice(0, 3)}`);
  const d = new Date(HAS_OFFSET_RE.test(iso) ? iso : `${iso}Z`);
  return Number.isFinite(d.getTime()) ? d : null;
}

/**
 * Internal: coerce input (Date | ISO string | number ms) → Date, or null when invalid/empty.
 * Thin alias of `parseTimestamp` so every formatter below shares one parse rule.
 */
function _toDate(input, opts) {
  return parseTimestamp(input, opts);
}

/** Berlin calendar Y/M/D of an instant, as numbers. */
function _berlinYmd(d) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BERLIN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const pick = (type) => Number(parts.find((p) => p.type === type)?.value);
  return { y: pick('year'), m: pick('month'), d: pick('day') };
}

/**
 * Whole calendar days between two instants, measured in Europe/Berlin.
 * Negative = `target` is in the past. Calendar-based on purpose: "Gestern"
 * means the previous Berlin day, not "between 24 and 48 hours ago".
 *
 * Exported because it is the fix for the anchor RESIDUAL this wave leaves
 * behind: `slaSummary.js` still counts days on a BROWSER-LOCAL anchor
 * (`localDateOnly` vs `todayLocalDateOnly`) while everything rendered next to
 * it is framed in Berlin. The two agree in Berlin and diverge elsewhere.
 * Unifying that arithmetic onto this helper is wave A5-4b's job.
 */
export function berlinDayDiff(target, nowDate) {
  const a = _berlinYmd(target);
  const b = _berlinYmd(nowDate);
  return Math.round(
    (Date.UTC(a.y, a.m - 1, a.d) - Date.UTC(b.y, b.m - 1, b.d)) / 86400000,
  );
}

/**
 * The Berlin calendar day of an instant, as a machine-readable `YYYY-MM-DD`.
 * This is what an HTML `<time datetime>` attribute needs when the visible text
 * is a Berlin date: `toISOString().slice(0, 10)` would emit the UTC day, which
 * for anything in the 22:00-24:00 UTC band is the day BEFORE the one on screen
 * — the precise contradiction this whole wave exists to remove.
 */
export function berlinDateISO(input, { dateOnly = false } = {}) {
  const d = _toDate(input, { dateOnly });
  if (!d) return null;
  const { y, m, d: day } = _berlinYmd(d);
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Format as Berlin date+time. Example: "11.05.2026, 21:37".
 * Falls back to the supplied placeholder when input is missing/invalid.
 */
export function formatBerlinDateTime(input, { placeholder = '—', withSeconds = false, dateOnly = false } = {}) {
  const d = _toDate(input, { dateOnly });
  if (!d) return placeholder;
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: BERLIN_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  }).format(d);
}

/**
 * Format as Berlin date only. Example: "11.05.2026".
 */
export function formatBerlinDate(input, { placeholder = '—', dateOnly = false } = {}) {
  const d = _toDate(input, { dateOnly });
  if (!d) return placeholder;
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: BERLIN_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Format as Berlin time only. Example: "21:37".
 */
export function formatBerlinTime(input, { placeholder = '—', withSeconds = false } = {}) {
  const d = _toDate(input);
  if (!d) return placeholder;
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: BERLIN_TZ,
    hour: '2-digit',
    minute: '2-digit',
    ...(withSeconds ? { second: '2-digit' } : {}),
  }).format(d);
}

/**
 * Format as short Berlin date. Example: "11.05." (no year) — useful for tight cells.
 */
export function formatBerlinShortDate(input, { placeholder = '—', dateOnly = false } = {}) {
  const d = _toDate(input, { dateOnly });
  if (!d) return placeholder;
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: BERLIN_TZ,
    day: '2-digit',
    month: '2-digit',
  }).format(d);
}

/**
 * Relative-time formatter with day-level granularity. The ladder, in order:
 *
 *   | distance                | reads                          |
 *   |-------------------------|--------------------------------|
 *   | < 10 s                  | "Gerade eben"                  |
 *   | < 1 min                 | "vor 30 Sekunden" (Intl)       |
 *   | < 1 h                   | "vor 5 Minuten"   (Intl)       |
 *   | < 24 h                  | "vor 4 Stunden"   (Intl)       |
 *   | previous / next Berlin day | "Gestern" / "Morgen"        |
 *   | within ±7 calendar days | "vor 3 Tagen" / "in 3 Tagen"   |
 *   | beyond that             | absolute Berlin date+time      |
 *
 * The day rung is CALENDAR-based (Europe/Berlin), so at 03:00 an event from
 * 23:00 the evening before still reads "vor 4 Stunden" — the hour rung wins
 * below 24 h — while a 30 h-old event reads "Gestern".
 *
 * Days are formatted explicitly rather than through `Intl.RelativeTimeFormat`
 * with `numeric: 'auto'`, because that emits "vorgestern" / "übermorgen" for
 * ±2 days, which nobody asked for and which reads worse than "vor 2 Tagen".
 *
 * `dateOnly: true` skips every sub-day rung: a date column has no time of day,
 * so "vor 21 Stunden" on a due DATE would be an invention. It reads
 * "Heute" / "Gestern" / "Morgen" / "vor N Tagen" and falls back to an absolute
 * DATE (never a fabricated time).
 *
 * `locale` (G12-V, default `'de'`) picks the word set: `'de'` is byte-identical
 * to the pre-G12 output, `'en'` renders "just now" / "Yesterday" / "3 days ago".
 * Any other value falls back to German (fail-closed) — the absolute fallbacks
 * stay `de-DE` in BOTH cases, see `RELATIV_WORTE`.
 *
 * WORDING + THRESHOLDS ARE A DOCUMENTED DEFAULT, NOT AN OWNER-CONFIRMED SPEC.
 * Plan §9 question 6 left the exact wording and cutoffs open and no owner
 * answer is on record; A5-4a picked the conservative option (German, matching
 * the `Intl` de-DE house style of the other rungs; "Gestern"/"Morgen"
 * capitalised as the owner spelled them; 7-day window so the "vor N Tagen"
 * rung the owner asked for actually has room to exist — the previous 48 h
 * cutoff left none). All of it is one constant away from being changed:
 * `JUST_NOW_SEC` and `DAY_WINDOW` at the top of this file.
 */
export function formatBerlinRelative(input, { placeholder = '—', now = Date.now(), dateOnly = false, locale = 'de' } = {}) {
  const d = _toDate(input, { dateOnly });
  if (!d) return placeholder;
  // 🔴 `'en'` only — any other value (including a stray object key such as
  // `constructor`) falls back to German instead of indexing the prototype.
  const worte = locale === 'en' ? RELATIV_WORTE.en : RELATIV_WORTE.de;
  const nowDate = new Date(now);
  const diffSec = Math.round((d.getTime() - nowDate.getTime()) / 1000);
  const absSec = Math.abs(diffSec);

  if (!dateOnly) {
    if (absSec < JUST_NOW_SEC) return worte.justNow;
    const rtf = new Intl.RelativeTimeFormat(worte.rtf, { numeric: 'auto' });
    if (absSec < 60) return rtf.format(diffSec, 'second');
    const diffMin = Math.round(diffSec / 60);
    if (Math.abs(diffMin) < 60) return rtf.format(diffMin, 'minute');
    if (absSec < 86400) return rtf.format(Math.round(diffMin / 60), 'hour');
  }

  const dayDiff = berlinDayDiff(d, nowDate);
  if (dayDiff === 0) return worte.today;
  if (dayDiff === -1) return worte.yesterday;
  if (dayDiff === 1) return worte.tomorrow;
  if (Math.abs(dayDiff) <= DAY_WINDOW) {
    return dayDiff < 0 ? worte.pastDays(-dayDiff) : worte.futureDays(dayDiff);
  }
  return dateOnly ? formatBerlinDate(d) : formatBerlinDateTime(d);
}
